import { Origins } from 'models/ScopeThreeCategoryOne/ScopeThreeCategoryOne.model';
import React, { FC, useEffect } from 'react';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Origin } from 'views/CreateProductFlow/CreateProductFlowInterfaces';
import { generateAddressLabel } from 'views/Scope3/Category4&9/LocationModal';
import { InputFieldProps } from 'shared/components/InputField';
import RHFInputField from 'shared/components/ReactHookFormFields/RHFInputField';
import FindLocationModal from 'shared/components/FindLocationModal';
import { LiveStockTypes } from 'enums/LiveStockEnums';

export interface FeedLocationModalInputFieldProps extends InputFieldProps {
  index: number;
  feedIndex: number;
  selectedOriginIndex: number;
  onSave: (data: Origins[]) => void;
}

const FeedLocationModalInputField: FC<FeedLocationModalInputFieldProps> = ({
  index,
  selectedOriginIndex,
  onSave,
  feedIndex,
  ...fieldProps
}) => {
  const { watch, setValue } = useFormContext();
  const [selectedIndex, setSelectedIndex] = useState<number>();
  const handleClick = () => setSelectedIndex(index);
  const basePath = `${LiveStockTypes.FEED_TRANSPORTATION}.${index}.feed.${feedIndex}`;
  const originPath = `${basePath}.origins`;
  const activeOrigins = watch(`${originPath}`);
  const isMultipleOrigin = watch(`${basePath}.multiOrigin`);
  useEffect(() => {
    if (!isMultipleOrigin) {
      const desiredAddress = watch(`${basePath}.address`);
      const originIndexAddress = watch(`${originPath}.0.address`);
      setValue(
        `${originPath}.0.address`,
        desiredAddress ? desiredAddress : originIndexAddress
      );
      setValue(
        `${originPath}.0.activityMappingRegionSpecId`,
        watch(`${basePath}.activityMappingRegionSpecId`)
      );
    }
  }, [isMultipleOrigin]);
  const handleSave = (data: Origins[]) => {
    if (onSave) onSave?.(data);
    else {
      const firstOrigin = data[0];
      if (data.length === 1) {
        data[0].share = null;
        setValue(`${originPath}`, data as Origins[]);
        const label =
          data[0]?.address && generateAddressLabel(data[0]?.address);
        setValue(`${basePath}.addressLabel`, label ?? '', {
          shouldValidate: true,
        });
        setValue(`${basePath}.address`, data[0]?.address, {
          shouldValidate: true,
        });
        setValue(
          `${basePath}.activityMappingRegionSpecId`,
          firstOrigin?.activityMappingRegionSpecId ||
            watch(`${basePath}.activityMappingRegionSpecId`),
          {
            shouldValidate: true,
          }
        );
        setValue(`${basePath}.multiOrigin`, false, {
          shouldValidate: true,
        });
        setValue(`${basePath}.bestFitRegion`, undefined);
      } else {
        setValue(`${originPath}`, data as Origins[]);
        setValue(`${basePath}.bestFitRegion`, undefined);
      }
      data?.map((origin, originIndex) => {
        const label = origin?.address && generateAddressLabel(origin?.address);
        setValue(`${originPath}.${originIndex}.addressLabel`, label ?? '', {
          shouldValidate: true,
        });
        setValue(
          `${originPath}.${originIndex}.activityMappingRegionSpecId`,
          origin.activityMappingRegionSpecId ?? '',
          { shouldValidate: true }
        );
        setValue(`${basePath}.bestFitRegion`, undefined);
      });
      setSelectedIndex(undefined);
    }
  };
  const handleClose = () => setSelectedIndex(undefined);
  const handleAddOrigin = () => {
    const desiredOrigins = watch(`${originPath}`);
    if (desiredOrigins.length === 1) {
      setValue(
        `${originPath}.0.addressLabel`,
        watch(`${basePath}.addressLabel`)
      );
      setValue(`${originPath}.0.id`, watch(`${basePath}.id`));
      setValue?.(`${basePath}.bestFitRegion`, undefined);
      setValue(`${basePath}.multiOrigin`, true);
      setValue(
        `${originPath}.0.activityMappingRegionSpecId`,
        watch(`${basePath}.activityMappingRegionSpecId`)
      );
      setValue?.(`${basePath}.addressLabel`, null);
      setValue?.(`${basePath}.activityMappingRegionSpecId`, null);
      setValue?.(`${basePath}.bestFitRegion`, null);
      setValue(`${basePath}.id`, undefined);
      setValue?.(`${basePath}.address`, null);
    }
    setValue(`${basePath}.multiOrigin`, true);
  };

  return (
    <div>
      <RHFInputField
        {...(fieldProps as InputFieldProps)}
        onClick={handleClick}
        readOnly
      />
      <FindLocationModal
        showModal={selectedIndex !== undefined}
        selectedIndexData={activeOrigins as Origin[]}
        onSave={handleSave}
        onCancel={handleClose}
        activeTabIndex={selectedOriginIndex}
        addCallBack={handleAddOrigin}
      />
    </div>
  );
};

export default FeedLocationModalInputField;
